import { useEffect, useMemo, useRef, useState } from "react";
import hljs from "highlight.js/lib/common";
import { Check, Copy } from "lucide-react";
import "highlight.js/styles/atom-one-light.css";

export default function CanvasCodeView({ code = "", language = "html", streaming = false }) {
  const [copied, setCopied] = useState(false);
  const scrollRef = useRef(null);

  // While streaming, keep the viewport pinned to the newest line
  useEffect(() => {
    if (!streaming) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [code, streaming]);

  const highlightedLines = useMemo(() => {
    const source = String(code || "");
    const escape = (s) =>
      s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    // While streaming, skip the (expensive) syntax highlight — re-highlighting a
    // large, growing document many times/sec can freeze the UI. Plain escaped
    // text keeps it smooth; the final view (not streaming) highlights fully.
    if (streaming) {
      return escape(source).split("\n");
    }
    let html = "";
    try {
      html = language && hljs.getLanguage(language)
        ? hljs.highlight(source, { language }).value
        : hljs.highlightAuto(source).value;
    } catch {
      html = escape(source);
    }
    // Split into lines but keep multi-line tokens intact
    return html.split("\n");
  }, [code, language, streaming]);

  const copyToClipboard = async () => {
    if (!navigator?.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(String(code || ""));
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#fafbfc]">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 bg-white/70 backdrop-blur-sm shrink-0">
        <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {language}
          {streaming && (
            <span className="inline-flex items-center gap-1 text-blue-500 normal-case tracking-normal">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
              writing…
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={copyToClipboard}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          title={copied ? "Copied" : "Copy code"}
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      {/* Code with line numbers */}
      <div ref={scrollRef} className="flex-1 overflow-auto custom-scroll">
        <table className="w-full border-collapse font-mono text-[12.5px] leading-[1.7]">
          <tbody>
            {highlightedLines.map((line, idx) => (
              <tr key={idx} className="hover:bg-slate-100/60 transition-colors">
                <td className="select-none text-right pr-4 pl-4 text-slate-300 w-[1%] whitespace-nowrap align-top border-r border-slate-100 sticky left-0 bg-[#fafbfc]">
                  {idx + 1}
                </td>
                <td className="pl-4 pr-6 align-top whitespace-pre">
                  <code
                    className="hljs bg-transparent p-0"
                    dangerouslySetInnerHTML={{ __html: line || "\u200b" }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
